import {FC, ReactNode, useContext, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Button, Menu, MenuItem, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter} from "@mui/x-data-grid-pro";
import {Construction} from "@mui/icons-material";
import {INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useSSTranslation } from "@/i18n/useSSTranslation";

const useStyles = makeStyles()((_theme) => ({
	customToolbar: {
		display: "flex",
		gap: "10px",
		border: "0px solid"
	},
	gap: {
		flexGrow: 100
	},
	subtitle: {
		fontWeight: 400,
		fontSize: "16px",
		letterSpacing: "0.15px",
		opacity: "0.8"
	}
}));

export interface IProps {
	type?: string,
	selected?: number[],
	onReset: () => void,
	canReset?: boolean,
	enableQuickFilter?: boolean,
	disableExport?: boolean,
	hideExport?: boolean;
	toolBarText?: string,
	exportToolTipText?: string,
	showAlternativeExportoption?: boolean;
	isExporting?: boolean;
	onClickAlternativeExportOption?: () => void;
	bulkActionOptions?: { label: string, onSelect: (label: string, selected: number[]) => void }[],
	customToolbarOptions?: ReactNode;
}

export const AdminTableCustomToolbar: FC<IProps> = ({
	bulkActionOptions,
	enableQuickFilter,
	canReset,
	onReset,
	type,
	disableExport,
	hideExport=false,
	selected,
	toolBarText,
	exportToolTipText,
	showAlternativeExportoption = false,
	isExporting = false,
	onClickAlternativeExportOption,
	customToolbarOptions
}) => {
	const { classes } = useStyles();
	const [bulkActionAnchorEl, setBulkActionAnchorEl] = useState<null | HTMLElement>(null);
	const { notify } = useContext(NotificationContext);

	return (
		<GridToolbarContainer className={classes.customToolbar} sx={{}}>
			{enableQuickFilter && <>
				<GridToolbarQuickFilter autoFocus />
				<Box className={classes.gap} />
			</>}
			<GridToolbarColumnsButton placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
			<GridToolbarFilterButton {...({} as any)} /> {/* crazy ts issue with mui 5 typing - cast here to squash */}
			{bulkActionOptions && (
				<Button
					disabled={!selected || selected.length === 0}
					aria-controls={bulkActionAnchorEl ? "bulk-action-menu" : undefined}
					id="bulk-actions-button"
					onClick={(e) => {
						if (selected && selected.length > 0)
							setBulkActionAnchorEl(e.currentTarget)
						else
							notify("", "Please select at least one " + type, INotificationSeverity.warning, 5000)
					}}
				>
					<Construction />
					Bulk Actions
				</Button>
			)}
			{
				!showAlternativeExportoption && !hideExport &&
				<Tooltip title={exportToolTipText} className="tooltip" placement="bottom">
					<span>
						<GridToolbarExport disabled={disableExport} />
					</span>
				</Tooltip>
			}
			{
				showAlternativeExportoption &&
				<CustomExportOption
					isExporting={isExporting}
					onClick={onClickAlternativeExportOption}
				/>
			}
			{
				customToolbarOptions
			}
			<Menu
				id="bulk-action-menu"
				anchorEl={bulkActionAnchorEl}
				open={Boolean(bulkActionAnchorEl)}
				onClose={() => setBulkActionAnchorEl(null)}
				MenuListProps={{
					"aria-labelledby": "bulk-actions-button"
				}}
				// Add disablePortal prop
				disablePortal
			>
				{bulkActionOptions?.map((e) => (
					<MenuItem
						key={e.label.replace(/\s/g, "")}
						onClick={() => {
							e.onSelect(e.label, selected ?? []);
							setBulkActionAnchorEl(null);
						}}
					>
						{e.label}
					</MenuItem>
				))}
			</Menu>
			<Box sx={{ flexGrow: 3 }}></Box>
			{toolBarText && <Typography className={classes.subtitle}>{toolBarText}</Typography>}
			{canReset && <Button onClick={onReset}>Reset</Button>}
			{/* <GridToolbarQuickFilter /> */}
		</GridToolbarContainer>
	);
};

interface ICustomExportOptionProps {
	isExporting: boolean;
	onClick: () => void;
}

const CustomExportOption: FC<ICustomExportOptionProps> = ({
	isExporting,
	onClick
}) => {
	const { t } = useSSTranslation();
	const theme = useTheme();
	const color = isExporting ? theme.palette.action.disabled : theme.palette.primary.main;

	return (
		<Stack
			direction="row"
			gap={1}
			onClick={onClick}
			sx={{ color, alignItems: "center", cursor: "pointer" }}
		>
			{
				isExporting ?
					<CircularProgress sx={{ color }} size="16px" /> :
					<FileDownloadOutlinedIcon
						sx={{
							color,
							"& path": {
								fill: `${color} !important`
							}
						}}
					/>
			}
			<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
				{t("common_export", "Export").toUpperCase()}
			</Typography>
		</Stack>
	);
}
